import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { usePendingChangesFeatureAccess } from '#hooks/usePendingChangesFeatureAccess';
import { useCurrentEnvironmentEntity } from '#reducers/projects';
import { combineAccessDecisions } from '#utils/access/combineAccessDecisions';
import type { AccessDecision } from '#utils/access/records/AccessDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { Environment } from '#utils/environments/records/Environment';
import type { Flag } from '#utils/flags/records/Flag';
import { createFlag } from '#utils/flags/records/Flag';
import { usePendingChangesAccessDecisions } from '#utils/pendingChanges/usePendingChangesAccessDecisions';

export type PendingChangesContextValue = Readonly<{
  enableFilterByApprovers?: boolean;
  environment: Environment;
  hasExpiringTargetChanges: boolean;
  approvalAccess: AccessDecision;
  scheduleAccess: AccessDecision;
  canBypassRequiredApprovals: boolean;
}>;

export const PendingChangesContext = createContext<PendingChangesContextValue>({} as PendingChangesContextValue);

export function PendingChangesProvider({
  environment,
  flag = createFlag(),
  hasExpiringTargetChanges = false,
  canBypassRequiredApprovals,
  children,
}: {
  environment?: Environment;
  flag?: Flag;
  hasExpiringTargetChanges?: boolean;
  canBypassRequiredApprovals?: boolean;
  children: ReactNode;
}) {
  const currentEnv = useCurrentEnvironmentEntity();
  const pendingChangesFeatureAccess = usePendingChangesFeatureAccess();
  const upgradeText = 'Upgrade your plan to use this feature';
  const { updateScheduledChanges } = usePendingChangesAccessDecisions(flag);

  const approvalAccess = createAccessDecision({
    isAllowed: pendingChangesFeatureAccess.approvals(),
    customMessage: upgradeText,
  });

  const scheduleAccess = combineAccessDecisions(
    createAccessDecision({
      isAllowed: pendingChangesFeatureAccess.scheduledChanges(),
      customMessage: upgradeText,
    }),
    updateScheduledChanges,
    createAccessDecision({
      isAllowed: !hasExpiringTargetChanges,
      customMessage: 'Cannot schedule to remove an individual context at a future date',
    }),
  );
  const env = environment || currentEnv;

  return (
    <PendingChangesContext.Provider
      value={{
        enableFilterByApprovers: pendingChangesFeatureAccess.filterByApprovers(),
        environment: env,
        hasExpiringTargetChanges,
        approvalAccess,
        scheduleAccess,
        canBypassRequiredApprovals: canBypassRequiredApprovals ?? flag.canBypassRequiredApprovals(env.key),
      }}
    >
      {children}
    </PendingChangesContext.Provider>
  );
}

export function usePendingChangesContext() {
  return useContext(PendingChangesContext);
}
